/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import {
  getUniversalTrackerValueLatestAttribute,
  UtrAttribute,
  UtrLookup
} from './index';
import { ActionCreator, createSelector } from '@reduxjs/toolkit';
import { getSuffix } from '../utils';
import { SUMMARY_DASHBOARD_CIRCLES } from '../constants/usage-slots';
import { RootState } from '../reducers';
import { UniversalTrackerPlain } from '../types/universalTracker';
import { ValueData } from '../types/surveyScope';
import { UtrValueFields, UtrvDataState } from '../types/universalTrackerValue';
import { MinimalUtrv } from '../utils/universalTrackerValueView';
import { SDG_CODES } from '../constants/utr';

export type Value = string | number | ValueData | UtrValueFields | undefined | UtrvDataState | MinimalUtrv;

export const getActual = (universalTracker: UtrLookup, attribute: UtrAttribute = null): ActionCreator<Value> => {
  return (dispatch, getState) => {
    const actual = getUniversalTrackerValueLatestAttribute(
      getState(),
      universalTracker,
      'actual',
      attribute
    );
    return actual || dispatch(getBaseline(universalTracker, attribute));
  };
};

export const getTarget = (universalTracker: UtrLookup, attribute: UtrAttribute = null): ActionCreator<Value> => {
  return (dispatch, getState) => {
    return getUniversalTrackerValueLatestAttribute(
      getState(),
      universalTracker,
      'target',
      attribute,
    );
  };
};

export const getBaseline = (universalTracker: UtrLookup, attribute: UtrAttribute = null): ActionCreator<Value> => {
  return (dispatch, getState) => {
    return getUniversalTrackerValueLatestAttribute(
      getState(),
      universalTracker,
      'baseline',
      attribute,
    );
  };
};


type ValueFieldType = number | undefined;
const convertUtrs = (state: RootState, universalTrackers: UtrLookup[]) => {
  // We pass value to ensure return type of (number | undefined)
  const attr = 'value';
  return universalTrackers.map(utr => {
    const actual = getUniversalTrackerValueLatestAttribute(state, utr, 'actual', attr) ?? getUniversalTrackerValueLatestAttribute(state, utr, 'baseline', attr);
    const target = getUniversalTrackerValueLatestAttribute(state, utr, 'target', attr);
    return (
      {
        name: utr.name,
        _id: utr._id,
        suffix: getSuffix(utr as { valueType: string }),
        actual: actual as ValueFieldType,
        target: target as ValueFieldType,
        allowDelete: true,
      }
    );
  });
};

const dashboardCircleUtrSelector = createSelector([
  (state: RootState) => state.initiative.data,
  (state: RootState) => state.universalTrackers.data,
], (initiative, utrs) => {
  const linkedUniversalTrackers = initiative && initiative.linkedUniversalTrackers;
  if (!linkedUniversalTrackers || !utrs) {
    return []
  }

  const utrIds = linkedUniversalTrackers
    .filter(tracker => tracker.usage && tracker.usage.includes(SUMMARY_DASHBOARD_CIRCLES))
    .map(matching => matching.universalTrackerId);

  return utrs.filter((utr) => utrIds.includes(utr._id));
});

const materialityUtrSelector = createSelector([
  (state: RootState) => state.initiative.data?.materiality,
  (state: RootState) => state.universalTrackers.data,
], (materiality, utrs) => {
  if (!materiality || !utrs) {
    return [];
  }
  return Object.keys(materiality)
    .filter((goal) => materiality[goal as any] === 100)
    .map(goal => utrs.find(utr => utr.code === `sdg/${goal}`))
    .filter(utr => !!utr) as UniversalTrackerPlain[];
})

const defaultSDGUtrSelector = createSelector([
  (state: RootState) => state.universalTrackers.data,
], (utrs) => {
  if (!utrs) {
    return []
  }

  return SDG_CODES.map((goal) => utrs.find((utr) => utr.code === `sdg/${goal}`)).filter(
    (utr) => !!utr
  ) as UniversalTrackerPlain[];
});

export const getBubbles = createSelector([
  materialityUtrSelector,
  dashboardCircleUtrSelector,
  defaultSDGUtrSelector,
  state => state,
], (materialityUtrs, keyUtrs, sdgUtrs, state) =>
({
  custom: convertUtrs(state, keyUtrs),
  materiality: convertUtrs(state, materialityUtrs),
  default: convertUtrs(state, sdgUtrs),
}));
